"use client";

/**
 * Armador de PACK VARIADO: reparte 7 o 14 unidades entre los sabores que
 * quieras, al precio prorrateado del pack de cada sabor.
 */

import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";

import { HeaderTitulo } from "@/components/app/chrome";
import { useApp } from "@/components/app/proveedor";
import { FotoBoot } from "@/components/app/foto-boot";
import { conDescuento, PrecioDist } from "@/components/app/precio-dist";
import { precioClave } from "@/components/app/presentaciones";
import type { ProductoApp } from "@/components/app/tipos";

export function PacksCliente({ productos }: { productos: ProductoApp[] }) {
  const router = useRouter();
  const { agregar, descuentoDist } = useApp();
  const [tam, setTam] = useState<7 | 14>(7);
  const [unidades, setUnidades] = useState<Record<string, number>>({});

  const total = useMemo(
    () => Object.values(unidades).reduce((t, n) => t + n, 0),
    [unidades],
  );

  const precio = useMemo(
    () =>
      Object.entries(unidades).reduce((t, [slug, n]) => {
        const p = productos.find((x) => x.slug === slug);
        if (!p) return t;
        const rate = precioClave(p, tam === 7 ? "mix7" : "mix14") ?? 0;
        return t + conDescuento(rate, descuentoDist) * n;
      }, 0),
    [unidades, productos, tam, descuentoDist],
  );

  function cambiar(slug: string, delta: number) {
    setUnidades((u) => {
      const actual = u[slug] ?? 0;
      const suma = Object.values(u).reduce((t, n) => t + n, 0);
      const nuevoValor = Math.max(0, actual + delta);
      // No dejar pasar del tamaño del pack.
      if (delta > 0 && suma >= tam) return u;
      const nuevo = { ...u, [slug]: nuevoValor };
      if (nuevo[slug] === 0) delete nuevo[slug];
      return nuevo;
    });
  }

  function cambiarTam(nuevo: 7 | 14) {
    setTam(nuevo);
    setUnidades({});
  }

  function agregarAlCarrito() {
    if (total !== tam) return;
    const pres = tam === 7 ? "mix7" : "mix14";
    for (const [slug, n] of Object.entries(unidades)) {
      agregar(`${slug}|${pres}`, n);
    }
    router.push("/pedido");
  }

  return (
    <>
      <HeaderTitulo titulo="Pack variado" />
      <main className="anim-bz-in min-h-(--alto-app) px-6 pt-[100px] pb-[190px]">
        <div className="pt-[18px]">
          <p className="text-sm leading-[21px] text-tinta/65">
            Combina los sabores que quieras hasta completar tu pack. Pagas el
            precio de pack de cada sabor.
          </p>

          <div className="mt-4 flex gap-2">
            {([7, 14] as const).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => cambiarTam(n)}
                className={`flex h-12 flex-1 items-center justify-center rounded-[24px] font-app text-[15px] font-semibold ${tam === n ? "bg-bosque text-white" : "bg-humo text-tinta"}`}
              >
                Pack de {n}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-col gap-2.5">
            {productos.map((p) => {
              const n = unidades[p.slug] ?? 0;
              const rate = precioClave(p, tam === 7 ? "mix7" : "mix14");
              return (
                <div
                  key={p.slug}
                  className="flex items-center gap-3.5 rounded-[22px] border border-black/5 bg-white p-3"
                >
                  <span
                    className="h-[56px] w-[56px] flex-none overflow-hidden rounded-2xl"
                    style={{ background: p.gradiente }}
                  >
                    <FotoBoot imagen={p.imagen} gradiente={p.gradiente} alt={p.nombre} sizes="56px" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-app text-[15px] font-semibold">
                      {p.nombre}
                    </span>
                    <span className="mt-0.5 block text-xs text-tinta/50">
                      <PrecioDist precio={rate} descuento={descuentoDist} /> c/u en
                      pack
                    </span>
                  </span>
                  <span className="flex h-[34px] items-center gap-2.5 rounded-[17px] bg-humo px-3">
                    <button
                      type="button"
                      aria-label="Menos"
                      onClick={() => cambiar(p.slug, -1)}
                      className="font-app text-[15px] font-semibold"
                    >
                      –
                    </button>
                    <span className="min-w-[20px] text-center font-app text-sm font-semibold">
                      {n}
                    </span>
                    <button
                      type="button"
                      aria-label="Más"
                      onClick={() => cambiar(p.slug, 1)}
                      className="font-app text-[15px] font-semibold"
                    >
                      +
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <div className="fixed bottom-0 left-1/2 z-30 w-full max-w-[430px] -translate-x-1/2 border-t border-black/5 bg-white/95 px-6 pt-[18px] pb-6">
        <div className="flex items-center justify-between">
          <span className="text-[13px] text-tinta/60">
            {total} de {tam} unidades
          </span>
          <span className="font-app text-xl font-black">${precio.toFixed(2)}</span>
        </div>
        <div className="mt-2 h-1.5 rounded-[3px] bg-humo">
          <div
            className="h-1.5 rounded-[3px] bg-lima transition-all"
            style={{ width: `${Math.min(100, (total / tam) * 100)}%` }}
          />
        </div>
        <button
          type="button"
          onClick={agregarAlCarrito}
          disabled={total !== tam}
          className="mt-3 flex h-14 w-full items-center justify-center rounded-[28px] bg-verde font-app text-base font-semibold text-white transition active:scale-[.98] disabled:opacity-60"
        >
          {total === tam
            ? "Agregar pack al carrito"
            : `Te faltan ${tam - total} unidades`}
        </button>
      </div>
    </>
  );
}
